import React, { useState } from 'react';
import axios from 'axios';
import { Bot, Sparkles, X, Send, TrendingUp, Calculator, ShieldCheck, Zap, ChevronRight, BarChart2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const get = (path) => axios.get(`${API}${path}`, { withCredentials: true }).then((r) => r.data);

const num = (v) =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? '—'
    : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// Every answer below comes from the bot's own APIs. The copilot never invents a signal, a price
// or a health status — on a live-money dashboard a made-up "96/100 HIGH CONFIDENCE" trade or a
// fake "CONNECTED, 100% ready" is worse than no answer.
async function signalsReply() {
  const d = await get('/api/signals?limit=5');
  const rows = (d?.signals || []).slice(0, 5);
  if (!rows.length) {
    return '🎯 **Live AI Signal Advisory** — no signals received yet. They appear here as soon as ' +
      'TradingView or the internal engine sends one.';
  }
  const lines = rows.map((s) =>
    `• ${s.symbol} ${s.action || s.signal} @ ${num(s.price)} | SL ${num(s.sl)} | TP ${num(s.tp)} | ` +
    `conf ${s.confidence ?? '—'}% | ${s.source || 'tradingview'}${s.strategy ? ` · ${s.strategy}` : ''}`);
  return `🎯 **Live AI Signal Advisory** — the latest REAL signals the bot received:\n${lines.join('\n')}\n\n` +
    'These are signals, not advice: the risk gate, cost floor and sizing decide whether each is traded.';
}

async function gateReply() {
  const d = await get('/api/system/strategy-gate');
  const rows = d?.gate || [];
  if (!rows.length) {
    return '🧪 **Strategy gate** — no walk-forward verdicts yet. Run the lab (System page → Strategy ' +
      'Gate → Run lab now). Until a pair is LIVE there, the internal engine only logs shadow signals.';
  }
  const live = rows.filter((r) => r.verdict === 'LIVE');
  const fmt = (r) => `• ${r.symbol} · ${r.strategy}: OOS ${r.oos?.trades ?? 0} trades, PF ${num(r.oos?.pf)}, net ₹${num(r.oos?.net)}`;
  return `🧪 **Strategy gate** — ${live.length} of ${rows.length} pairs proven out-of-sample:\n` +
    (live.length ? live.map(fmt).join('\n') : '• none yet — nothing trades real money through the engine') +
    '\n\nLIVE = OOS PF ≥ 1.25 over ≥ 25 unseen trades, after real charges.';
}

async function healthReply() {
  const d = await get('/api/system/status');
  const svc = d?.services || {};
  const risk = d?.risk_engine || {};
  return '🛡️ **System status (live)**:\n' +
    `• Broker: ${svc.broker || 'unknown'}\n• MongoDB: ${svc.mongodb || 'unknown'}\n` +
    `• Redis: ${svc.redis || 'unknown'}\n• Risk engine: ${risk.risk_locked ? 'LOCKED' : 'active'} ` +
    `(open trades: ${risk.open_trades ?? 0})\n• Mode: ${String(d?.mode || 'live').toUpperCase()}`;
}

const SIZING_TEXT = '📐 **How LARA sizes and filters a trade** (defaults — your Settings may differ):\n' +
  '• Options: lots = capital slice × option_capital_deploy_pct ÷ (premium × lot size); the strike ' +
  'walks out from ATM (max 5) until one lot fits.\n' +
  '• Cost floor: the 70% target must pay ≥ 3× the round-trip charges (~₹51 per MCX option) or the ' +
  'signal is skipped.\n' +
  '• Exits: chart SL, or 70% of the way to the chart target; trend-ride can let a winner run.\n' +
  '• Ruin guards: halt at 30% drawdown from the equity peak, or PF < 0.9 after 25 closed trades.';

const HELP_TEXT = '💡 I answer from the bot\'s real data only: latest signals, the strategy gate, ' +
  'live system status, and how sizing works. I don\'t make up trade ideas.';

function routeQuery(q) {
  const lower = q.toLowerCase();
  if (/(gate|proven|strateg|engine|walk)/.test(lower)) return gateReply;
  if (/(signal|trend|entry|\btp\b|\bsl\b)/.test(lower)) return signalsReply;
  if (/(health|audit|status|connect|broker)/.test(lower)) return healthReply;
  if (/(lot|size|sizing|cost|floor|risk|calculat)/.test(lower)) return async () => SIZING_TEXT;
  return async () => HELP_TEXT;
}

export function LaraCopilotModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'copilot',
      text: "👋 Hi! I'm the LARA Copilot. I read the bot's live data — signals, the strategy gate, system status — and explain how sizing works. What do you want to check?",
      time: 'Just now'
    }
  ]);
  const [input, setInput] = useState('');

  const promptChips = [
    { label: "⚡ Latest real signals (Entry / TP / SL)", query: "Show the latest signals with entry, TP and SL" },
    { label: "🧪 Which strategies are proven? (gate)", query: "Which strategies passed the gate?" },
    { label: "🛡️ Live system status", query: "Run system health status check" },
    { label: "📐 How are lot size & cost floor calculated?", query: "How does LARA calculate lot size and the cost floor?" }
  ];

  const handleSend = async (textToSend) => {
    const query = textToSend || input;
    if (!query.trim()) return;

    const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [...prev, { sender: 'user', text: query, time: now() }]);
    if (!textToSend) setInput('');

    let replyText;
    try {
      replyText = await routeQuery(query)();
    } catch (err) {
      const code = err?.response?.status;
      replyText = code === 401
        ? '🔒 Please log in again — the bot did not accept this session.'
        : `⚠️ Couldn't load that from the bot${code ? ` (HTTP ${code})` : ''}. Is the backend running?`;
    }
    setMessages(prev => [...prev, { sender: 'copilot', text: replyText, time: now() }]);
  };

  return (
    <>
      {/* Floating Toggle Button */}
      <button
        data-testid="copilot-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-cyan-600 text-white rounded-full shadow-lg hover:shadow-cyan-500/20 hover:scale-105 transition-all font-semibold text-xs border border-cyan-400/30"
      >
        <Sparkles className="w-4 h-4 animate-spin-slow text-amber-300" />
        <span>LARA Copilot</span>
        <span className="bg-emerald-950 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded font-mono border border-emerald-500/30">LIVE DATA</span>
      </button>

      {/* Floating Modal Window */}
      {isOpen && (
        <div data-testid="copilot-modal" className="fixed bottom-14 left-2 right-2 sm:left-auto sm:right-5 sm:bottom-20 z-50 w-auto sm:w-full max-w-[420px] bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden flex flex-col h-[500px] sm:h-[560px] animate-in fade-in slide-in-from-bottom-5 duration-200">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-zinc-900 via-zinc-900 to-zinc-950 p-3 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-zinc-100">Nextora AI Advisory Copilot</h3>
                  <span className="text-[9px] bg-cyan-950 text-cyan-300 border border-cyan-700/50 px-1.5 py-0.2 rounded uppercase font-semibold tracking-wider">LIVE DATA</span>
                </div>
                <p className="text-[10px] text-zinc-400">Answers from the bot's real data — never invented signals</p>
              </div>
            </div>
            <button
              data-testid="copilot-close-btn"
              onClick={() => setIsOpen(false)}
              className="text-zinc-400 hover:text-zinc-200 p-1 rounded-lg hover:bg-zinc-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Sub-header Banner */}
          <div className="bg-cyan-950/30 border-b border-cyan-900/30 px-3 py-1.5 text-[11px] text-cyan-300 flex items-center gap-1.5 font-medium">
            <Zap className="w-3.5 h-3.5 text-cyan-400" />
            <span>Reads: signals • strategy gate • system status</span>
          </div>

          {/* Messages Body */}
          <div className="flex-1 p-3 overflow-y-auto space-y-3 bg-zinc-950/80">
            {messages.map((m, idx) => (
              <div key={idx} className={`flex flex-col ${m.sender === 'user' ? 'items-end' : 'items-start'}`}>
                <div className={`p-3 rounded-lg text-xs leading-relaxed max-w-[90%] ${
                  m.sender === 'user'
                    ? 'bg-cyan-600 text-white rounded-br-none'
                    : 'bg-zinc-900 border border-zinc-800 text-zinc-200 rounded-bl-none shadow-sm'
                }`}>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
                <span className="text-[9px] text-zinc-500 mt-1 px-1">{m.time}</span>
              </div>
            ))}

            {/* Prompt Chips */}
            <div className="pt-2 space-y-1.5">
              <p className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 px-1">Ask about:</p>
              {promptChips.map((chip, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSend(chip.query)}
                  className="w-full text-left text-xs bg-zinc-900/90 hover:bg-zinc-800/90 text-cyan-300 hover:text-cyan-200 p-2 rounded-lg border border-zinc-800 hover:border-cyan-500/40 transition-all flex items-center justify-between group"
                >
                  <span>{chip.label}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-cyan-400 group-hover:translate-x-0.5 transition-all" />
                </button>
              ))}
            </div>
          </div>

          {/* Quick Action Navigation Bar */}
          <div className="bg-zinc-900/90 border-t border-zinc-800 px-2 py-1.5 flex gap-1 overflow-x-auto text-[10px]">
            <button onClick={() => handleSend("Latest signals")} className="px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded flex items-center gap-1 shrink-0">
              <TrendingUp className="w-3 h-3 text-cyan-400" /> Signals
            </button>
            <button onClick={() => handleSend("Strategy gate")} className="px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded flex items-center gap-1 shrink-0">
              <BarChart2 className="w-3 h-3 text-emerald-400" /> Strategy Gate
            </button>
            <button onClick={() => handleSend("How is lot size calculated")} className="px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded flex items-center gap-1 shrink-0">
              <Calculator className="w-3 h-3 text-amber-400" /> Sizing Rules
            </button>
            <button onClick={() => handleSend("System health status")} className="px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded flex items-center gap-1 shrink-0">
              <ShieldCheck className="w-3 h-3 text-purple-400" /> System Status
            </button>
          </div>

          {/* Input Footer */}
          <div className="p-2 bg-zinc-950 border-t border-zinc-800 flex items-center gap-2">
            <input
              type="text"
              data-testid="copilot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask about signals, the strategy gate, status, sizing..."
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-cyan-500"
            />
            <button
              data-testid="copilot-send-btn"
              onClick={() => handleSend()}
              className="bg-cyan-600 hover:bg-cyan-500 text-white p-2 rounded-lg transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
